import { useMemo, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import type { InvoiceTransactionDraftDTO, InvestorListDTO } from '@inversor-hub/types';
import { useListInvoiceTransactionDrafts } from '@/lib/api/investment-transactions';
import { useGetInvoiceForwardingAddress } from '@/lib/api/me';
import useSnackbar from '@/hooks/useSnackbar';
import { useAnalytics } from '@/hooks/useAnalytics';
import { DataEntryBar } from './InvoiceDraftsPanel/DataEntryBar';
import { InvoiceInboxDrawer } from './InvoiceDraftsPanel/InvoiceInboxDrawer';
import { ReviewInvoiceDraftDialog } from './InvoiceDraftsPanel/ReviewInvoiceDraftDialog';
import { UploadInvoiceDialog } from './InvoiceDraftsPanel/UploadInvoiceDialog';
import { TransactionImportWizard } from './TransactionImportWizard';

type PanelModal = 'upload' | 'inbox' | 'import' | null;
type ReviewOrigin = 'inbox' | 'direct' | null;

interface InvoiceDraftsPanelProps {
  investments: InvestorListDTO[];
}

export const InvoiceDraftsPanel = ({ investments }: InvoiceDraftsPanelProps) => {
  const { show } = useSnackbar();
  const { trackEvent } = useAnalytics();
  const { data: forwardingAddress } = useGetInvoiceForwardingAddress();
  const { data: drafts, isLoading } = useListInvoiceTransactionDrafts();

  const [modal, setModal] = useState<PanelModal>(null);
  const [reviewing, setReviewing] = useState<InvoiceTransactionDraftDTO | null>(null);
  const [origin, setOrigin] = useState<ReviewOrigin>(null);
  const [reviewedCount, setReviewedCount] = useState(0);

  const pendingCount = drafts?.length ?? 0;

  const reviewingDraft = useMemo(
    () => (reviewing ? (drafts?.find((draft) => draft.id === reviewing.id) ?? reviewing) : null),
    [drafts, reviewing]
  );

  const startReview = (draft: InvoiceTransactionDraftDTO, from: Exclude<ReviewOrigin, null>) => {
    setOrigin(from);
    setReviewing(draft);
    setModal(null);
  };

  const closeReview = (returnToInbox: boolean) => {
    setReviewing(null);
    setModal(returnToInbox && origin === 'inbox' ? 'inbox' : null);
    setOrigin(null);
  };

  const handleUploaded = (draft: InvoiceTransactionDraftDTO) => {
    setModal(null);
    startReview(draft, 'direct');
  };

  if (isLoading) {
    return <Skeleton variant="rectangular" height={92} sx={{ borderRadius: '16px', mb: 2.75 }} />;
  }

  return (
    <Box sx={{ mb: 2.75 }}>
      <DataEntryBar
        count={pendingCount}
        canImport={investments.length > 0}
        onUpload={() => setModal('upload')}
        onOpenInbox={() => setModal('inbox')}
        onImport={() => setModal('import')}
      />

      {modal === 'import' && (
        <TransactionImportWizard
          open
          investments={investments}
          onClose={() => setModal(null)}
          onImported={() => show({ title: 'Importación completada', variant: 'success' })}
        />
      )}

      <UploadInvoiceDialog
        open={modal === 'upload'}
        forwardingAddress={forwardingAddress?.address}
        onClose={() => setModal(null)}
        onUploaded={handleUploaded}
      />

      <InvoiceInboxDrawer
        open={modal === 'inbox'}
        drafts={drafts ?? []}
        reviewedCount={reviewedCount}
        onClose={() => setModal(null)}
        onReview={(draft) => startReview(draft, 'inbox')}
      />

      {reviewingDraft && (
        <ReviewInvoiceDraftDialog
          key={reviewingDraft.id}
          draft={reviewingDraft}
          investments={investments}
          onConfirmed={(name) => {
            setReviewedCount((value) => value + 1);
            show({ title: `Transacción guardada${name ? ` en ${name}` : ''}`, variant: 'success' });
            closeReview(true);
          }}
          onDiscarded={() => {
            show({ title: 'Borrador descartado', variant: 'success' });
            closeReview(true);
          }}
          onClose={() => closeReview(true)}
          trackEvent={trackEvent}
          show={show}
        />
      )}
    </Box>
  );
};
